import { DataRole } from '@mode-switch/express';
import { QueryResult } from '@mode-switch/query';
import { isEqual } from 'lodash';
import { Encoding } from '@mode-vegas/vegas';

import * as ChartActions from './chart/chart.actions';

import {
  backfillFlatTableColumnSettings,
  backfillFlatTableGeneralFormat,
  getDefaultFieldFormatByColumn,
  getDefaultFieldFormatMapFromColumns,
  getDefaultSettingsByColumns,
  getDefaultGeneralFormat,
  generateColumnSettingsFromUpdatedEncoding,
  updateFormatWithFlatTableColumns,
} from '@mode/shared/util-js';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { Switch } from '@mode/shared/util-switch';
import { Flamingo, TableEncodingHelpers } from '@mode/charts/util-vegas';

type ChartFormat = ChartTypes.ChartFormat;
type FieldFormatMap = ChartTypes.FieldFormatMap;

/**
 * Helper functions to expedite the state related format processing. These functions relate
 * very closely to encoding, so they exist in the charts feature with encoding related things.
 */

/**
 * Helper function to test if a format update is necessary in response to an encoding update.
 *
 * For example, now that pills can be reordered, there are cases where encoding changes
 * don't actually need a format update. Just simply testing if all table cols in encoding
 * are the same.
 */
export function requiresFormatUpdateFromEncodingChange(oldEncoding: Encoding | undefined, newEncoding: Encoding) {
  if (typeof oldEncoding === 'undefined') {
    return true;
  }

  const oldFormulas = oldEncoding.marks.properties.text.values.map((value) => value.formula),
    newFormulas = newEncoding.marks.properties.text.values.map((value) => value.formula);

  return !isEqual(oldFormulas.sort(), newFormulas.sort());
}

/**
 * Helper to do the all of the NEW flatTable format backfill logic and return a filled format.
 */
export function backfillFlatTableVizFormat(
  chart: ChartTypes.EncodedChart,
  switchModel: FlamingoTypes.ModeModel,
  view: FlamingoTypes.ModeView
): ChartFormat {
  // If no blob, we need to add a stub and potentially add column formats if necessary,
  // else, we just need to backfill missing general settings.
  let flatTableFormat: ChartFormat['flatTable'];

  // If columns exist in the table, create the result columns that we'll need to generate formatting.
  let columns: QueryResult.Column<DataRole>[] = [];
  if (chart.chartType === 'table' && TableEncodingHelpers.hasFields(chart.encoding)) {
    const newSwitchModel = Switch.Views.merge(switchModel, view);
    columns = Flamingo.tableColumns(newSwitchModel, chart.queryToken, chart.encoding);
  }

  if (!chart.format.flatTable) {
    // flatTable blob doesn't even exist, very old flat tables. In this cases, we generate the blob
    // for all viz types, and if a table, generate columns as well.

    // Create stub
    flatTableFormat = {
      general: getDefaultGeneralFormat(ChartTypes.FlatTableDefaultGeneralFormat.FlatTableViz),
      columns: [],
    };
    // Generate default column formats if columns exist in encoding
    if (columns) {
      flatTableFormat = {
        ...flatTableFormat,
        columns: getDefaultSettingsByColumns(columns),
      };
    }
  } else {
    // flatTable blob exists, but either new general format options have been added, or `format.flatTable.columns` are in old format
    flatTableFormat = backfillFlatTableGeneralFormat(chart.format.flatTable);
    if (columns) {
      flatTableFormat = backfillFlatTableColumnSettings(flatTableFormat, columns);
    }
  }

  const filledFormat = {
    ...chart.format,
    flatTable: flatTableFormat,
  };

  return filledFormat;
}

/**
 * Helper to do the all of the NEW flatTable fieldFormat backfill.
 */
export function backfillFlatTableFieldFormat(
  chart: ChartTypes.EncodedChart,
  switchModel: FlamingoTypes.ModeModel,
  view: FlamingoTypes.ModeView
): FieldFormatMap {
  // If no blob, we need to add a stub and potentially add column formats if necessary,
  // else, we just need to backfill missing general settings.
  // let flatTableFormat: ChartFormat['flatTable'];
  const newSwitchModel = Switch.Views.merge(switchModel, view);
  const columns = Flamingo.tableColumns(newSwitchModel, chart.queryToken, chart.encoding);

  const fieldFormatMap = getDefaultFieldFormatMapFromColumns(columns);

  return fieldFormatMap;
}

/**
 * Update the fieldFormats blob on encoding update.
 *
 * This will remove fieldFormats no longer present, and create new defaults for new columns.
 */
export function generateFieldFormatsFromUpdatedEncoding(
  currentColumns: QueryResult.Column<DataRole>[],
  previousFieldFormats: FieldFormatMap
): FieldFormatMap {
  const previousFieldKeys = previousFieldFormats ? Object.keys(previousFieldFormats) : [];
  const remainingFieldFormats: FieldFormatMap = {};

  // Filter out removed columns
  const remainingFieldKeys = previousFieldKeys.filter(
    (fieldFormula) => currentColumns.findIndex((col) => col.source === fieldFormula) !== -1
  );

  // Confirm remaining columns are still the same type, if not, reset to default for colType,
  // this wouldn't be column, but it's possible a column could retain the same name but
  // change type.
  remainingFieldKeys.forEach((fieldFormula) => {
    const newColumn = currentColumns.find((col) => col.source === fieldFormula);
    const oldFormat = previousFieldFormats[fieldFormula];
    const oldFormatDefaultPane =
      oldFormat && oldFormat.paneFormat && oldFormat.paneFormat[ChartTypes.FieldPaneTypes.Default];

    if (!newColumn) {
      return;
    }

    if (oldFormatDefaultPane && oldFormatDefaultPane.colType === newColumn.type.name) {
      remainingFieldFormats[fieldFormula] = oldFormat;
    } else {
      // get new default field format by column type
      remainingFieldFormats[fieldFormula] = getDefaultFieldFormatByColumn(newColumn);
    }
  });

  // Get any net new columns now present in encoding
  const newColumns = currentColumns.filter(
    (col) => remainingFieldKeys.findIndex((formula) => formula === col.source) === -1
  );

  // Generate new default formats for all new columns
  newColumns.forEach((newColumn) => {
    remainingFieldFormats[newColumn.source] = getDefaultFieldFormatByColumn(newColumn);
  });

  return remainingFieldFormats;
}

/**
 * Helper to get backfills that have been generated (format and fieldFormats) by a viz token.
 * This is specifically used for the explorer case where only a single chart is reduced.
 *
 * Returns filled viz, or original if no backfills are present.
 */
export function getBackfilledVizByToken(
  action: ReturnType<typeof ChartActions.backfillTableFormats>,
  viz: ChartTypes.EncodedChart
): ChartTypes.EncodedChart {
  const filledFieldFormatObject = action.filledFieldFormatMaps.find((item) => item.token === viz.token);
  const filledFormatObject = action.filledFormats.find((item) => item.token === viz.token);

  if (filledFieldFormatObject || filledFormatObject) {
    let filledViz = { ...viz };

    if (filledFormatObject) {
      filledViz = {
        ...filledViz,
        format: filledFormatObject.filledFormat,
      };
    }

    if (filledFieldFormatObject) {
      filledViz = {
        ...filledViz,
        fieldFormats: filledFieldFormatObject.filledFieldFormats,
      };
    }

    return filledViz;
  }

  return viz;
}

/**
 * Combines multiple backfill functions to return the expected format config for a table given an encoding change
 */
export function getUpdatedTableFormat(
  model: FlamingoTypes.ModeModel,
  queryToken: string,
  encoding: Encoding,
  fieldFormats: FieldFormatMap,
  format: ChartFormat
) {
  // Get previous columns formats, and new columns on updating encoding
  const currentColumns = Flamingo.tableColumns(model, queryToken, encoding);

  // Remove fieldFormats that are no longer present in encoding and add default format for newly added columns
  const newFieldFormats = generateFieldFormatsFromUpdatedEncoding(currentColumns, fieldFormats);

  // Remove column format settings that are no longer present in encoding and add default settings for newly added columns
  const columnFormats = generateColumnSettingsFromUpdatedEncoding(currentColumns, format.flatTable);

  // Generate new chart format object
  const chartFormat = updateFormatWithFlatTableColumns(columnFormats, format);

  return {
    fieldFormats: newFieldFormats,
    format: chartFormat,
  };
}
